import ReactDatePicker from 'react-datepicker';
import { displayDateFormatWithDay } from '@/constants/dates';
import 'react-datepicker/dist/react-datepicker.css';
import IconCatch from '@/components/icons/icon-catch/icon-catch';
import { useEffect, useMemo, useState } from 'react';
export interface DatePickerProps {
  initialDate?: string | null;
  onDateChange?: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  onError?: (error: string) => void;
}

const createDateWithoutTimezoneIssues = (dateString: string | Date | null): Date | null => {
  if (!dateString) return null;

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date');
    }
    date.setUTCHours(12, 0, 0, 0); // Set the time to noon UTC to avoid any timezone-related date shifting
    return date;
  } catch {
    return null;
  }
};

export default function DatePicker(props: DatePickerProps) {
  const { initialDate, onDateChange, minDate, maxDate, onError } = props;

  const adjustedInitialDate = useMemo(
    () => (initialDate ? createDateWithoutTimezoneIssues(initialDate) : null),
    [initialDate],
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(adjustedInitialDate);

  useEffect(() => {
    if (adjustedInitialDate && !validateDate(adjustedInitialDate)) {
      setSelectedDate(null);
    } else {
      setSelectedDate(adjustedInitialDate);
    }
  }, [adjustedInitialDate]);

  const validateDate = (date: Date | null): boolean => {
    if (!date) return true;

    if (minDate && date < minDate) {
      onError?.('Date cannot be earlier than the minimum allowed date');
      return false;
    }

    if (maxDate && date > maxDate) {
      onError?.('Date cannot be later than the maximum allowed date');
      return false;
    }

    return true;
  };

  const handleDateChange = (date: Date | null) => {
    if (date && !validateDate(date)) {
      return;
    }

    if (date) {
      setSelectedDate(date);
      onDateChange?.(date);
    } else {
      setSelectedDate(null);
      onDateChange?.(null);
    }
  };

  return (
    <div className="mb-4 flex max-w-[170px] items-center rounded" role="group" aria-label="Date picker">
      <div className="flex h-10 items-center rounded-l border border-r-0 border-[#183344] bg-[#183344] p-2">
        <IconCatch icon="Calendar-Add" size="xs" bgColor="transparent" filter="brightness(0) invert(1)" />
      </div>
      <div className="border-ui-element-divider bg-background-page h-10 flex-1 rounded-r border border-l-0">
        <ReactDatePicker
          selected={selectedDate}
          onChange={handleDateChange}
          dateFormat={displayDateFormatWithDay}
          placeholderText="Set target date"
          minDate={minDate}
          maxDate={maxDate}
          customInput={
            <input
              className="h-10 w-full border-0 bg-transparent px-3 text-center text-base outline-none"
              aria-label="Select target date"
              aria-describedby="date-picker-help"
            />
          }
          ariaLabelledBy="date-picker-label"
        />
      </div>
    </div>
  );
}
